// keyrd_console/src/components/PilotInvites.js
import React, { useEffect, useMemo, useState } from "react";
import { getPilotInvites } from "../api/pilotAdmin";
import PilotGovernanceBanner from "./PilotGovernanceBanner";
import PilotStatusPill from "./PilotStatusPill";
import PilotDataSourceBadge from "./PilotDataSourceBadge";


function PCUatSyntheticInviteControl() {
  const [apiBase, setApiBase] = useState("https://staging-api.keyrd.org");
  const [token, setToken] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const trimmedApiBase = apiBase.trim().replace(/\/$/, "");
  const trimmedToken = token.trim();
  const trimmedLabel = label.trim();
  const labelLooksBounded = trimmedLabel.startsWith("pc-uat-synthetic-patient-");
  const canIssue = Boolean(trimmedApiBase && trimmedToken && labelLooksBounded && !busy);

  async function issueInvite() {
    if (!canIssue) {
      setError("Enter API base, a valid RDN bearer token, and a label beginning pc-uat-synthetic-patient-.");
      setMessage("");
      return;
    }

    setBusy(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch(`${trimmedApiBase}/console/pilot/invites`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${trimmedToken}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          label: trimmedLabel,
          notes: trimmedLabel,
          synthetic_actor_identifier: trimmedLabel,
          requested_invite_count: 1,
          pc_uat: true,
          bounded_synthetic_invite: true,
        }),
      });

      let data = {};
      try {
        data = await response.json();
      } catch (err) {
        data = {};
      }

      if (!response.ok) {
        throw new Error(data.error || data.message || `Invite request failed with HTTP ${response.status}`);
      }

      const inviteCode =
        data.invite_code ||
        data.inviteCode ||
        data.code ||
        (data.invite && (data.invite.invite_code || data.invite.inviteCode || data.invite.code)) ||
        "";

      if (!inviteCode) {
        throw new Error("Invite request completed but no invite code was returned.");
      }

      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(inviteCode);
      }

      setMessage("Exactly one bounded synthetic invite was generated and copied locally. Do not paste it into chat.");
      setToken("");
    } catch (err) {
      setError(err && err.message ? err.message : "Invite generation failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-pc-uat-c13r8="bounded-synthetic-invite-control"
      style={{
        marginTop: 16,
        marginBottom: 16,
        padding: 16,
        borderRadius: 12,
        border: "1px solid #bfdbfe",
        backgroundColor: "#eff6ff",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>
        PC-UAT bounded synthetic invite issuance
      </div>
      <div style={{ fontSize: 13, color: "#334155", marginBottom: 10 }}>
        Generates exactly one bounded synthetic invite. The invite code is copied locally only and must not be pasted into chat.
      </div>

      <input
        type="text"
        value={apiBase}
        onChange={(e) => setApiBase(e.target.value)}
        placeholder="https://staging-api.keyrd.org"
        style={{
          width: "100%",
          padding: 10,
          borderRadius: 8,
          border: "1px solid #cbd5e1",
          marginBottom: 10,
        }}
      />

      <input
        type="password"
        value={token}
        onChange={(e) => setToken(e.target.value)}
        placeholder="Paste fresh RDN bearer token here. Do not include Bearer."
        style={{
          width: "100%",
          padding: 10,
          borderRadius: 8,
          border: "1px solid #cbd5e1",
          marginBottom: 10,
        }}
      />

      <input
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="pc-uat-synthetic-patient-YYYYMMDD_HHMMSS"
        style={{
          width: "100%",
          padding: 10,
          borderRadius: 8,
          border: "1px solid #cbd5e1",
          marginBottom: 10,
        }}
      />

      <button
        type="button"
        onClick={issueInvite}
        disabled={!canIssue}
        style={{
          padding: "10px 14px",
          borderRadius: 8,
          border: "1px solid #0f766e",
          backgroundColor: canIssue ? "#0f766e" : "#94a3b8",
          color: "white",
          fontWeight: 700,
          cursor: canIssue ? "pointer" : "not-allowed",
        }}
      >
        {busy ? "Generating..." : "Generate exactly one synthetic invite"}
      </button>

      {!labelLooksBounded && label && (
        <div style={{ marginTop: 8, fontSize: 12, color: "#b45309" }}>
          Label must start with pc-uat-synthetic-patient-.
        </div>
      )}

      {message && (
        <div style={{ marginTop: 10, fontSize: 13, color: "#166534" }}>
          {message}
        </div>
      )}

      {error && (
        <div style={{ marginTop: 10, fontSize: 13, color: "#b91c1c" }}>
          {error}
        </div>
      )}
    </div>
  );
}


const pageStyle = {
  padding: 20,
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const titleRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 12,
};

const titleStyle = {
  fontSize: 24,
  fontWeight: 700,
  color: "#0f172a",
  margin: 0,
};

const subtitleStyle = {
  color: "#475569",
  fontSize: 14,
  marginTop: 4,
};

const controlRowStyle = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
};

const inputStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  minWidth: 260,
};

const selectStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
};

const tableWrapStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  overflow: "hidden",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
};

const thStyle = {
  textAlign: "left",
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#475569",
  background: "#f8fafc",
  borderBottom: "1px solid #e2e8f0",
  padding: "12px 10px",
  verticalAlign: "top",
};

const tdStyle = {
  padding: "12px 10px",
  borderBottom: "1px solid #e2e8f0",
  verticalAlign: "top",
  fontSize: 14,
  color: "#0f172a",
};

const clickableRowStyle = {
  cursor: "pointer",
  background: "#ffffff",
};

const metaTextStyle = {
  fontSize: 12,
  color: "#64748b",
};

const loadingStyle = {
  padding: 18,
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#334155",
};

const errorStyle = {
  padding: 14,
  borderRadius: 10,
  background: "#fef2f2",
  color: "#991b1b",
  border: "1px solid #fecaca",
};

const emptyStyle = {
  padding: 18,
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#475569",
};

function normalizeRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.invites)) return payload.invites;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
}

function matchesQuery(row, query) {
  if (!query) return true;
  const haystack = [
    row.invite_code,
    row.invite_label,
    row.invite_status,
    row.cohort,
    row.participant_uuid,
    row.participant_label,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(query.toLowerCase());
}

function filterRows(rows, query, inviteStatus) {
  return rows.filter((row) => {
    const queryOk = matchesQuery(row, query);
    const statusOk =
      inviteStatus === "all" ||
      String(row.invite_status || "").toLowerCase() === inviteStatus;
    return queryOk && statusOk;
  });
}

function PilotInvites({ auth, onSelectParticipant }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [query, setQuery] = useState("");
  const [inviteStatus, setInviteStatus] = useState("all");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const res = await getPilotInvites(auth);
        if (!cancelled) {
          setPayload(res?.data || res || {});
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load pilot invites.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  const data = payload || {};
  const rows = normalizeRows(data);
  const filteredRows = useMemo(
    () => filterRows(rows, query, inviteStatus),
    [rows, query, inviteStatus]
  );

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={loadingStyle}>Loading invites / cohorts…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={errorStyle}>{error}</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={titleRowStyle}>
        <div>
          <h1 style={titleStyle}>Invites / Cohorts</h1>
          <div style={subtitleStyle}>
            Durable visibility for invite issuance, status, participant binding, and cohort assignment.
          </div>
        </div>
        <PilotDataSourceBadge source={data.data_source} stub={Boolean(data.stub)} />
      </div>

      <PilotGovernanceBanner auth={auth} system={data.system || data} />

      <div style={controlRowStyle}>
        <input
          style={inputStyle}
          type="text"
          value={query}
          placeholder="Search invite code, label, cohort, participant…"
          onChange={(e) => setQuery(e.target.value)}
        />
        
      <PCUatSyntheticInviteControl />

<select
          style={selectStyle}
          value={inviteStatus}
          onChange={(e) => setInviteStatus(e.target.value)}
        >
          <option value="all">All invite statuses</option>
          <option value="valid">Valid</option>
          <option value="used">Used</option>
          <option value="expired">Expired</option>
          <option value="paused">Paused</option>
          <option value="held_for_review">Held for Review</option>
        </select>
      </div>

      {!filteredRows.length ? (
        <div style={emptyStyle}>No invites matched the current filters.</div>
      ) : (
        <div style={tableWrapStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Invite</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Participant Binding</th>
                <th style={thStyle}>Cohort</th>
                <th style={thStyle}>Issued / Notes</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row, idx) => (
                <tr
                  key={`${row.invite_code || row.invite_label || "invite"}-${idx}`}
                  style={clickableRowStyle}
                  onClick={() =>
                    typeof onSelectParticipant === "function" &&
                    row.participant_uuid &&
                    onSelectParticipant(row.participant_uuid)
                  }
                >
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 700 }}>{row.invite_label || row.invite_code || "Unnamed Invite"}</div>
                    <div style={metaTextStyle}>{row.invite_code || "No invite code"}</div>
                  </td>

                  <td style={tdStyle}>
                    <PilotStatusPill
                      status={row.invite_status || "unknown"}
                      label={row.invite_status || "unknown"}
                    />
                  </td>

                  <td style={tdStyle}>
                    <div>{row.participant_label || "Unbound"}</div>
                    <div style={metaTextStyle}>{row.participant_uuid || "No participant UUID"}</div>
                  </td>

                  <td style={tdStyle}>
                    <div>{row.cohort || "No cohort"}</div>
                  </td>

                  <td style={tdStyle}>
                    <div>{row.issued_at_utc || "Unknown issued time"}</div>
                    <div style={metaTextStyle}>{row.notes || "No notes"}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PilotInvites;